import React from 'react';
import {
  Paper,
  Stack,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  Chip,
  Tooltip,
  IconButton,
  Typography,
  FormHelperText,
  InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import HelpIcon from '../../help/HelpIcon';

const outlinedInputSx = {
  color: 'var(--wb-text)',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-blue-strong)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-blue)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-blue-light)' }
};

const selectInputSx = {
  ...outlinedInputSx,
  '& .MuiOutlinedInput-input': { py: '6px', px: '12px' }
};

const MetadataSection = ({
  formValues,
  errors,
  databaseOptions,
  connectionOptions,
  resolvedAccountId,
  accountName,
  selectedConnection,
  labelColor,
  accentColor,
  onFieldChange,
  onConnectionChange,
  onRefreshDatabases,
  onRefreshConnections,
  isRefreshingDatabases,
  isRefreshingConnections,
  onTagInputChange,
  onAddTag,
  onRemoveTag,
  onToggleActive
}) => (
  <Paper
    elevation={0}
    sx={{
      bgcolor: 'var(--wb-x-bg-141b2d, #141b2d)',
      border: '1px solid var(--wb-x-border-3b82f659, rgba(59, 130, 246, 0.35))',
      borderRadius: 0,
      p: 3
    }}
  >
    <Typography variant="subtitle1" sx={{ color: 'var(--wb-accent)', fontWeight: 600, mb: 2 }}>
      Información base
    </Typography>

    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl fullWidth error={Boolean(errors.semantic_database_id)}>
            <InputLabel id="semantic-table-database-label" sx={labelColor}>
              Base semántica *
            </InputLabel>
            <Select
              labelId="semantic-table-database-label"
              label="Base semántica"
              value={formValues.semantic_database_id}
              onChange={onFieldChange('semantic_database_id')}
              sx={selectInputSx}
            >
              {databaseOptions.length === 0 && (
                <MenuItem value="" disabled>
                  No hay bases registradas
                </MenuItem>
              )}
              {databaseOptions.map((database) => {
                const value =
                  database?.semantic_database_id ||
                  database?.semanticDatabaseId ||
                  database?.id ||
                  database?.ID ||
                  '';
                const name = database?.name || database?.display_name || database?.displayName || value;
                const rawDescription = database?.description || database?.DESCRIPTION;
                const description =
                  typeof rawDescription === 'string'
                    ? rawDescription
                        .split(/\s+/)
                        .filter(Boolean)
                        .slice(0, 10)
                        .join(' ')
                    : '';
                const optionLabel = description ? name + ' · ' + description : name;
                return (
                  <MenuItem key={value} value={value}>
                    {optionLabel}
                  </MenuItem>
                );
              })}
            </Select>
            {errors.semantic_database_id && (
              <FormHelperText>{errors.semantic_database_id}</FormHelperText>
            )}
          </FormControl>
          <HelpIcon module="modelo-semantico" topic="tabla-semantica" anchor="base-de-datos-semántica" />
          <Tooltip title="Actualizar bases">
            <span>
              <IconButton
                onClick={onRefreshDatabases}
                disabled={isRefreshingDatabases || !onRefreshDatabases}
                sx={{ color: accentColor }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper
          elevation={0}
          sx={{
            bgcolor: 'var(--wb-x-bg-111827, #111827)',
            border: '1px solid var(--wb-x-border-3b82f640, rgba(59, 130, 246, 0.25))',
            borderRadius: 0,
            p: 2.5
          }}
        >
          <Typography variant="subtitle2" sx={{ color: 'var(--wb-accent)', fontWeight: 600, mb: 1 }}>
            Cuenta asociada
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--wb-text)' }}>
            ID: {resolvedAccountId || 'No disponible'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--wb-text-label)', mt: 0.5 }}>
            Nombre: {accountName || 'No disponible'}
          </Typography>
          {errors.account_id && (
            <Typography variant="caption" sx={{ color: 'var(--wb-danger-light)', display: 'block', mt: 1 }}>
              {errors.account_id}
            </Typography>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl fullWidth error={Boolean(errors.connection_id)}>
            <InputLabel id="semantic-table-connection-label" sx={labelColor}>
              Conexión *
            </InputLabel>
            <Select
              labelId="semantic-table-connection-label"
              label="Conexión"
              value={formValues.connection_id}
              onChange={onConnectionChange}
              sx={selectInputSx}
            >
              {connectionOptions.length === 0 && (
                <MenuItem value="" disabled>
                  No hay conexiones disponibles
                </MenuItem>
              )}
              {connectionOptions.map((connection, index) => {
                const value =
                  connection?.connection_id ||
                  connection?.connectionId ||
                  connection?.id ||
                  connection?.ID ||
                  'connection-' + index;
                const name =
                  connection?.connection_name ||
                  connection?.connectionName ||
                  connection?.name ||
                  value;
                const type = connection?.connection_type || connection?.type || 'N/D';
                const rawDescription = connection?.description || connection?.connection_description;
                const description =
                  typeof rawDescription === 'string'
                    ? rawDescription
                        .split(/\s+/)
                        .filter(Boolean)
                        .slice(0, 10)
                        .join(' ')
                    : '';
                const optionLabel = description ? name + ' · ' + type + ' · ' + description : name + ' · ' + type;
                return (
                  <MenuItem key={value} value={value}>
                    {optionLabel}
                  </MenuItem>
                );
              })}
            </Select>
            {errors.connection_id && (
              <FormHelperText>{errors.connection_id}</FormHelperText>
            )}
          </FormControl>
          <HelpIcon module="modelo-semantico" topic="tabla-semantica" anchor="conexión" />
          <Tooltip title="Actualizar conexiones">
            <span>
              <IconButton
                onClick={onRefreshConnections}
                disabled={isRefreshingConnections || !onRefreshConnections}
                sx={{ color: accentColor }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Catálogo"
          value={formValues.catalog}
          onChange={onFieldChange('catalog')}
          fullWidth
          helperText={
            selectedConnection
              ? 'Sugerido por la conexión ' + (selectedConnection.connection_type || selectedConnection.type || '')
              : 'Opcional según motor'
          }
          InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
          InputProps={{ sx: outlinedInputSx }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          label="Base de datos *"
          value={formValues.database}
          onChange={onFieldChange('database')}
          fullWidth
          error={Boolean(errors.database)}
          helperText={errors.database}
          InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
          InputProps={{ sx: outlinedInputSx }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Esquema *"
          value={formValues.schema}
          onChange={onFieldChange('schema')}
          fullWidth
          error={Boolean(errors.schema)}
          helperText={errors.schema}
          InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
          InputProps={{ sx: outlinedInputSx }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          label="Tabla física *"
          value={formValues.table_name}
          onChange={onFieldChange('table_name')}
          fullWidth
          error={Boolean(errors.table_name)}
          helperText={errors.table_name}
          InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
          InputProps={{ sx: outlinedInputSx }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          label="Nombre legible"
          value={formValues.display_name}
          onChange={onFieldChange('display_name')}
          fullWidth
          InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
          InputProps={{
            sx: outlinedInputSx,
            endAdornment: (
              <InputAdornment position="end">
                <HelpIcon module="modelo-semantico" topic="tabla-semantica" anchor="nombre-para-mostrar" />
              </InputAdornment>
            )
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Stack direction="row" spacing={1} alignItems="center">
        <FormControl fullWidth>
          <InputLabel sx={labelColor}>Dialecto SQL</InputLabel>
          <Select
            label="Dialecto SQL"
            value={formValues.sql_dialect}
            onChange={onFieldChange('sql_dialect')}
            sx={selectInputSx}
          >
            <MenuItem value="">No especificar</MenuItem>
            {['snowflake', 'postgres', 'mysql', 'mssql', 'oracle'].map((dialect) => (
              <MenuItem key={dialect} value={dialect}>
                {dialect}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <HelpIcon module="modelo-semantico" topic="tabla-semantica" anchor="dialecto-sql" />
        </Stack>
      </Grid>

      <Grid item xs={12}>
        <TextField
          label="Descripción"
          value={formValues.description}
          onChange={onFieldChange('description')}
          fullWidth
          multiline
          minRows={3}
          InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
          InputProps={{
            sx: outlinedInputSx,
            endAdornment: (
              <InputAdornment position="end">
                <HelpIcon module="modelo-semantico" topic="tabla-semantica" anchor="descripción" />
              </InputAdornment>
            )
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Agregar etiqueta"
            value={formValues.tagInput}
            onChange={onTagInputChange}
            size="small"
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: outlinedInputSx }}
          />
          <HelpIcon module="modelo-semantico" topic="tabla-semantica" anchor="etiquetas" />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddTag}
            disabled={!formValues.tagInput.trim()}
            sx={{ bgcolor: 'var(--wb-accent)', color: 'var(--wb-bg-surface)', '&:hover': { bgcolor: 'var(--wb-blue)' } }}
          >
            Añadir
          </Button>
          {formValues.tags.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {formValues.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => onRemoveTag(tag)}
                  deleteIcon={<DeleteIcon />}
                  sx={{ bgcolor: 'var(--wb-x-bg-60a5fa2e, rgba(96, 165, 250, 0.18))', color: 'var(--wb-x-text-fde68a, #fde68a)' }}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Grid>

      <Grid item xs={12}>
        <Stack direction="row" spacing={0.5} alignItems="center">
        <FormControlLabel
          control={
            <Switch
              checked={formValues.is_active}
              onChange={onToggleActive}
              sx={{
                '& .MuiSwitch-switchBase': {
                  color: 'var(--wb-accent)'
                },
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'var(--wb-accent)'
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'var(--wb-x-bg-38bdf8a6, rgba(56, 189, 248, 0.65))'
                },
                '& .MuiSwitch-track': {
                  backgroundColor: 'var(--wb-blue-40)'
                }
              }}
            />
          }
          label={formValues.is_active ? 'Entidad activa' : 'Entidad inactiva'}
          sx={{ color: 'var(--wb-text-strong)' }}
        />
        <HelpIcon module="modelo-semantico" topic="tabla-semantica" anchor="activa" />
        </Stack>
      </Grid>
    </Grid>
  </Paper>
);

export default MetadataSection;
